import { createContext, ReactNode, useEffect, useState } from "react";
import { boundingBox, excludeIds, orgId } from "../utils/dashboardUtils";
import { BoundingBox, LayerData } from "../services/types";

// Define the shape of your context data
interface LayersContextType {
    layers: LayerData[];
    loading: boolean;
    fetchLayersData: (orgId: number, boundingBox: BoundingBox | null, excludeIds: string[] | null) => void;
}

// Create the context with default values
const LayersContext = createContext<LayersContextType>({
    layers: [],
    loading: true,
    fetchLayersData: () => {},
});

interface LayersProviderProps {
    children: ReactNode;
}

// Define the provider for the context
export const LayersProvider = ({ children }: LayersProviderProps) => {
    const [layers, setLayers] = useState<LayerData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchLayersData = async (orgId: number, boundingBox: BoundingBox | null, excludeIds: string[] | null) => {
        if (!orgId) {
            setLoading(false);
            console.log('No orgId provided to fetch layers');
            return;
        }

        setLoading(true);
        try {
            // Fetch layers
            const layersResponse = await fetch(
                `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayersbysearch?orgId=${orgId}&externalId=*`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!layersResponse.ok) {
                throw new Error('Failed to fetch layers');
            }

            const layersResult = await layersResponse.json();
            console.log('layers result:', layersResult);

            let fetchedLayers: LayerData[] = layersResult.data || [];

            // Update state with filtered layers (without items initially)
            setLayers(fetchedLayers);

            if (fetchedLayers.length > 0) {
                // Construct boundingBox query parameters
                const boundingBoxParams = boundingBox 
                ? `&boundingBox=${encodeURIComponent(JSON.stringify(boundingBox))}` 
                : "";

                const excludeIdsParam = excludeIds ? `&excludeIds=${excludeIds.join(",")}` : "";

                // Fetch items for all layers concurrently
                const itemsPromises = fetchedLayers.map(layer => 
                    fetch(
                        `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayeritems?layerId=${layer.layer_id}${boundingBoxParams}${excludeIdsParam}`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    )
                    .then(response => response.ok ? response.json() : Promise.reject('Failed to fetch layer items'))
                    .catch(error => {
                        console.error(`Error fetching items for layer ${layer.layer_id}:`, error);
                        return [{ items: [], summary: [] }]; // Ensure failure doesn't break entire function
                    })
                );

                // Wait for all layer fetches to complete
                const itemsResults = await Promise.all(itemsPromises);

                // Update each layer with its corresponding items
                const layersWithItems = fetchedLayers.map((layer, index) => ({
                    ...layer,
                    items: itemsResults[index].items,
                    summary: itemsResults[index].summary,
                }));

                setLayers(layersWithItems);
            }
        } catch (error: any) {
            console.log('Error fetching layers or items:', error.message);
            setLayers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (orgId) {
            fetchLayersData(orgId, boundingBox, excludeIds);
        }
    }, [orgId, boundingBox, excludeIds]);

    return (
        <LayersContext.Provider value={{ layers, loading, fetchLayersData }}>
            {children}
        </LayersContext.Provider>
    );
};

// Export the hook to use the context
export default LayersContext;